import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Plus, DollarSign, X, Percent, Activity, Pencil, Trash2 } from "lucide-react";

interface PayoutRevenue {
  id: number;
  offerId: number;
  entryName: string;
  payoutType: string;
  payoutAmount: number;
  payoutPercentage: number;
  revenueType: string;
  revenueAmount: number;
  revenuePercentage: number;
  isDynamicRevenue: boolean;
  isDefault: boolean;
  isPrivate: boolean;
  isPostbackDisabled: boolean;
  isEnforceCaps: boolean;
}

const PAYOUT_TYPES = [
  { value: "cpa", label: "CPA", description: "Cost Per Action — fixed payout per conversion" },
  { value: "cpc", label: "CPC", description: "Cost Per Click — fixed payout per click" },
  { value: "cpm", label: "CPM", description: "Cost Per Mille — fixed payout per 1000 impressions" },
  { value: "cps", label: "CPS", description: "Cost Per Sale — fixed payout per sale" },
  { value: "cpi", label: "CPI", description: "Cost Per Install — fixed payout per install" },
  { value: "revshare", label: "RevShare", description: "Revenue Share — payout is a % of revenue" },
];

const REVENUE_TYPES = [
  { value: "rpa", label: "RPA", description: "Revenue Per Action" },
  { value: "rpc", label: "RPC", description: "Revenue Per Click" },
  { value: "rpm", label: "RPM", description: "Revenue Per Mille" },
  { value: "rps", label: "RPS", description: "Revenue Per Sale" },
  { value: "rpi", label: "RPI", description: "Revenue Per Install" },
];

function formatPayoutDisplay(p: PayoutRevenue): string {
  if (p.payoutType === "revshare") {
    return `${p.payoutPercentage}% of revenue`;
  }
  return `$${(p.payoutAmount ?? 0).toFixed(2)}`;
}

function formatRevenueDisplay(p: PayoutRevenue): string {
  if (p.isDynamicRevenue) {
    return "Dynamic (per conversion)";
  }
  return `$${(p.revenueAmount ?? 0).toFixed(2)}`;
}

export function OfferPayoutsTab({ offerId }: { offerId: string }) {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingPayout, setEditingPayout] = useState<PayoutRevenue | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (payoutId: number) => {
      const res = await api.delete(`/v1/offers/${offerId}/payouts/${payoutId}`);
      if (!res.ok) throw new Error("Failed to delete");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["offers", offerId, "payouts"] }),
  });

  const { data, isLoading } = useQuery({
    queryKey: ["offers", offerId, "payouts"],
    queryFn: async () => {
      const res = await api.get(`/v1/offers/${offerId}/payouts`);
      if (!res.ok) throw new Error("Failed to fetch payouts");
      return res.json();
    },
  });

  const createMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const res = await api.post(`/v1/offers/${offerId}/payouts`, payload);
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || "Failed to create payout");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "payouts"] });
      setShowModal(false);
    },
  });

  const payouts: PayoutRevenue[] = data?.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-700">
          {payouts.length} payout entr{payouts.length !== 1 ? "ies" : "y"}
        </h3>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex items-center gap-2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
        >
          <Plus className="w-4 h-4" /> Add Payout Entry
        </button>
      </div>

      {isLoading ? (
        <div className="h-32 bg-gray-100 rounded-xl animate-pulse" />
      ) : payouts.length === 0 ? (
        <div className="text-center py-8 bg-white rounded-xl border border-gray-200">
          <DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No payout entries configured.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {payouts.map((payout) => (
            <div key={payout.id} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-gray-900">{payout.entryName}</h4>
                    {payout.isDefault && (
                      <span className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs font-medium">
                        Default
                      </span>
                    )}
                    {payout.isPrivate && (
                      <span className="inline-flex items-center rounded-full bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 text-xs font-medium">
                        Private
                      </span>
                    )}
                    {payout.payoutType === "revshare" && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-purple-50 text-purple-700 border border-purple-200 px-2 py-0.5 text-xs font-medium">
                        <Percent className="w-3 h-3" /> RevShare
                      </span>
                    )}
                    {payout.isDynamicRevenue && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 text-xs font-medium">
                        <Activity className="w-3 h-3" /> Dynamic Revenue
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Payout</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {PAYOUT_TYPES.find((t) => t.value === payout.payoutType)?.label || (payout.payoutType ?? "").toUpperCase()}{" "}
                        — {formatPayoutDisplay(payout)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Revenue</p>
                      <p className="text-sm font-semibold text-gray-900">
                        {REVENUE_TYPES.find((t) => t.value === payout.revenueType)?.label || (payout.revenueType ?? "").toUpperCase()}{" "}
                        — {formatRevenueDisplay(payout)}
                      </p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1 ml-3 shrink-0">
                  <button
                    onClick={() => setEditingPayout(payout)}
                    className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { if (window.confirm(`Delete "${payout.entryName}"?`)) deleteMutation.mutate(payout.id); }}
                    disabled={deleteMutation.isPending}
                    className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <PayoutFormModal
          onClose={() => setShowModal(false)}
          onCreate={(payload) => createMutation.mutate(payload)}
          isLoading={createMutation.isPending}
        />
      )}

      {editingPayout && (
        <EditPayoutModal
          offerId={offerId}
          payout={editingPayout}
          onClose={() => setEditingPayout(null)}
        />
      )}
    </div>
  );
}

function PayoutFormModal({ onClose, onCreate, isLoading }: {
  onClose: () => void;
  onCreate: (payload: Record<string, unknown>) => void;
  isLoading: boolean;
}) {
  const [entryName, setEntryName] = useState("");
  const [payoutType, setPayoutType] = useState("cpa");
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutPercentage, setPayoutPercentage] = useState("");
  const [revenueType, setRevenueType] = useState("rpa");
  const [revenueAmount, setRevenueAmount] = useState("");
  const [isDynamicRevenue, setIsDynamicRevenue] = useState(false);
  const [isDefault, setIsDefault] = useState(false);
  const [error, setError] = useState("");

  const isRevShare = payoutType === "revshare";

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");

    if (!entryName.trim()) {
      setError("Entry name is required");
      return;
    }

    if (isRevShare) {
      const pct = Number(payoutPercentage);
      if (isNaN(pct) || pct < 0 || pct > 100) {
        setError("Payout percentage must be between 0 and 100");
        return;
      }
    } else {
      const amt = Number(payoutAmount);
      if (isNaN(amt) || amt < 0) {
        setError("Payout amount must be a valid number");
        return;
      }
    }

    if (!isDynamicRevenue) {
      const revAmt = Number(revenueAmount);
      if (isNaN(revAmt) || revAmt < 0) {
        setError("Revenue amount must be a valid number");
        return;
      }
    }

    onCreate({
      entryName: entryName.trim(),
      payoutType,
      payoutAmount: isRevShare ? 0 : Number(payoutAmount),
      payoutPercentage: isRevShare ? Number(payoutPercentage) : 0,
      revenueType,
      revenueAmount: isDynamicRevenue ? 0 : Number(revenueAmount),
      isDynamicRevenue,
      isDefault,
    });
  }

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Add Payout Entry</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Name *</label>
            <input type="text" required value={entryName} onChange={(e) => setEntryName(e.target.value)} className={inputCls} placeholder="e.g., Default Payout" />
          </div>

          {/* Payout section */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Payout (what you pay the affiliate)</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type *</label>
                <select required value={payoutType} onChange={(e) => setPayoutType(e.target.value)} className={inputCls}>
                  {PAYOUT_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  {PAYOUT_TYPES.find((t) => t.value === payoutType)?.description}
                </p>
              </div>
              <div>
                {isRevShare ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Share % *</label>
                    <div className="relative">
                      <input type="number" required min="0" max="100" step="0.1" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} className={inputCls} placeholder="e.g., 30" />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">%</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">Affiliate gets this % of revenue per conversion</p>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payout Amount *</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                      <input type="number" required min="0" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className={`${inputCls} pl-7`} placeholder="0.00" />
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Revenue section */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">Revenue (what the advertiser pays you)</h3>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isDynamicRevenue} onChange={(e) => setIsDynamicRevenue(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
                <span className="text-xs font-medium text-amber-700">Dynamic revenue</span>
              </label>
            </div>

            {isDynamicRevenue && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                <p className="text-xs text-amber-800">
                  Revenue amount will be passed dynamically with each conversion postback via the <code className="bg-amber-100 px-1 rounded">revenue</code> or <code className="bg-amber-100 px-1 rounded">sale_amount</code> parameter.
                  Each conversion can have a different revenue value.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Type *</label>
                <select required value={revenueType} onChange={(e) => setRevenueType(e.target.value)} className={inputCls}>
                  {REVENUE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>
              {!isDynamicRevenue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Amount *</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
                    <input type="number" required min="0" step="0.01" value={revenueAmount} onChange={(e) => setRevenueAmount(e.target.value)} className={`${inputCls} pl-7`} placeholder="0.00" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* RevShare + Dynamic info */}
          {isRevShare && isDynamicRevenue && (
            <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3">
              <p className="text-xs text-purple-800 font-medium mb-1">RevShare + Dynamic Revenue</p>
              <p className="text-xs text-purple-700">
                At conversion time, the payout will be calculated as: <code className="bg-purple-100 px-1 rounded">revenue × {payoutPercentage || "??"}%</code>.
                Since revenue is dynamic, each conversion's payout will vary based on the revenue value received.
              </p>
            </div>
          )}

          <div className="flex items-center gap-2">
            <input type="checkbox" id="isDefault" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
            <label htmlFor="isDefault" className="text-sm font-medium text-gray-700">Set as default payout entry</label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {isLoading ? "Creating..." : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditPayoutModal({ offerId, payout, onClose }: { offerId: string; payout: PayoutRevenue; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [entryName, setEntryName] = useState(payout.entryName || "");
  const [payoutType, setPayoutType] = useState(payout.payoutType || "cpa");
  const [payoutAmount, setPayoutAmount] = useState(String(payout.payoutAmount ?? 0));
  const [payoutPercentage, setPayoutPercentage] = useState(String(payout.payoutPercentage ?? 0));
  const [revenueType, setRevenueType] = useState(payout.revenueType || "rpa");
  const [revenueAmount, setRevenueAmount] = useState(String(payout.revenueAmount ?? 0));
  const [isDynamicRevenue, setIsDynamicRevenue] = useState(payout.isDynamicRevenue || false);
  const [isDefault, setIsDefault] = useState(payout.isDefault || false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const isRevShare = payoutType === "revshare";

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.patch(`/v1/offers/${offerId}/payouts/${payout.id}`, {
        entryName: entryName.trim(),
        payoutType,
        payoutAmount: isRevShare ? 0 : Number(payoutAmount),
        payoutPercentage: isRevShare ? Number(payoutPercentage) : 0,
        revenueType,
        revenueAmount: isDynamicRevenue ? 0 : Number(revenueAmount),
        isDynamicRevenue,
        isDefault,
      });
      if (!res.ok) {
        const d = await res.json();
        throw new Error(d.error || "Failed to update");
      }
      queryClient.invalidateQueries({ queryKey: ["offers", offerId, "payouts"] });
      onClose();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const inputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-2xl p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold">Edit Payout Entry</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg border border-red-200">{error}</div>}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Entry Name *</label>
            <input type="text" required value={entryName} onChange={(e) => setEntryName(e.target.value)} className={inputCls} />
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-medium text-gray-900">Payout</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Payout Type</label>
                <select value={payoutType} onChange={(e) => setPayoutType(e.target.value)} className={inputCls}>
                  {PAYOUT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                {isRevShare ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Share %</label>
                    <input type="number" min="0" max="100" step="0.1" value={payoutPercentage} onChange={(e) => setPayoutPercentage(e.target.value)} className={inputCls} />
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Payout Amount</label>
                    <input type="number" min="0" step="0.01" value={payoutAmount} onChange={(e) => setPayoutAmount(e.target.value)} className={inputCls} />
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-gray-900">Revenue</h3>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={isDynamicRevenue} onChange={(e) => setIsDynamicRevenue(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
                <span className="text-xs font-medium text-amber-700">Dynamic</span>
              </label>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Type</label>
                <select value={revenueType} onChange={(e) => setRevenueType(e.target.value)} className={inputCls}>
                  {REVENUE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {!isDynamicRevenue && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Revenue Amount</label>
                  <input type="number" min="0" step="0.01" value={revenueAmount} onChange={(e) => setRevenueAmount(e.target.value)} className={inputCls} />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input type="checkbox" id="editIsDefault" checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" />
            <label htmlFor="editIsDefault" className="text-sm font-medium text-gray-700">Set as default</label>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={loading} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
              {loading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
